// In-memory cart store for the v2 POS architecture.
//
// This is the FE source of truth between cashier interactions. It deliberately
// lives alongside the existing `posStore` -- the UI cutover is a later PR. When
// that lands, the cart slice in posStore can be deleted.
//
// State persistence: the cart's lines + customer + discount are persisted
// to localStorage so a tab refresh or accidental close doesn't lose work. The
// last preview response is persisted too, marked 'stale' on rehydrate, so
// totals/tax survive a refresh and a genuine preview failure degrades to the
// last server-priced numbers instead of $0 tax. The next preview call
// re-confirms and snaps Tier 1 back to live data.
//
// What this store DOES:
//   - Hold the in-memory cart
//   - Expose mutators (addLine, updateQty, removeLine, setCustomer, etc.)
//   - Expose preview-callback handlers (used by useCartPreview)
//   - Expose a `replaceFromCommit` for after a successful /cart/commit
//
// What this store does NOT do:
//   - Fire /cart/preview itself; the page wires `useCartPreview(cart, ...)`
//     and forwards callbacks here
//   - Process payment; the [Pay] handler builds a request via
//     `buildCommitRequest` and calls `useCommitCart`
//   - Talk to BroadcastChannel; that's a separate `cartChannel.ts` (Phase 1
//     follow-up) that subscribes to this store's state and rebroadcasts.

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  Cart,
  CartDiscount,
  CartLine,
  CartLoyalty,
  CartPartialPayment,
  CartPreviewStatus,
  PreviewResponse,
} from '@/lib/cart'
import {
  applyPreviewToLines,
  emptyCart,
  newCartLine,
} from '@/lib/cart'
import type { Product } from '@/types'
import type { PreviewErrorInfo } from '@/api/v2'
import {
  setCommittedReceiptSink,
  setDiscountApprovalSource,
  setPreviewReceiptIdSource,
} from '@/api/cart'
import {
  DISCOUNT_EXCEEDS_LIMIT,
  PIN_THROTTLED,
  addApproval,
  consumePrimedPreview,
  dropRefusedApprovals,
  isOverrideErrorCode,
  keepApprovalsOnRebind,
  pruneApprovals,
  removeApprovalsBySignature,
  type PrimedPreview,
  type DiscountApproval,
} from '@/lib/discountLimit'
import type { DiscountOverrideRow } from '@/lib/cart'
import { useSessionStore } from './sessionStore'
// posStore is the source of truth for the partial-payment predicate.
// The import is cyclic (posStore imports useCartStore for its
// clearReceipt/clearCart side-effects), but both directions only
// USE the imported symbols inside function bodies, never at module
// evaluation time -- so ESM's cyclic-import live-binding resolves
// cleanly by the time these mutators run.
import { usePOSStore, isReceiptPartiallyPaid } from './posStore'

interface CartActions {
  // ---- Mutators ----
  addLine: (args: {
    product: Product
    qty?: number
    lot_number?: string | null
  }) => void
  updateQty: (localId: string, qty: number) => void
  removeLine: (localId: string) => void
  setCustomer: (customer_id: number | null) => void
  setExemptTaxes: (exempt: boolean) => void
  /** Wholesale clear -- call after a successful commit, or when the cashier
   *  cancels the sale. */
  clear: () => void
  /** Replace the cart with a held receipt's contents. Caller derives the
   *  CartLines from the held receipt's items list. */
  loadFromUnhold: (lines: CartLine[], customer_id: number | null) => void

  // ---- Preview lifecycle ----
  /** Called by useCartPreview when a preview fetch is queued for `fingerprint`. */
  setPreviewPending: (fingerprint: string) => void
  /** Called by useCartPreview when a preview response arrives. We re-check
   *  the fingerprint vs the current cart so a late-arriving response for
   *  a stale shape is dropped on the floor. */
  applyPreview: (preview: PreviewResponse, firedFingerprint: string,
                 currentFingerprint: string) => void
  /** Called by useCartPreview / the bridge self-heal loop on fetch error.
   *  Cart state is preserved so the cashier keeps seeing the local estimate;
   *  checkout stays gated until a preview re-confirms. `info` carries the
   *  classified reason (auth/transport/rejected/offline/transient) so the UI
   *  can message truthfully instead of always saying "Reconnecting". Omit it
   *  to flip to 'error' without disturbing a previously-captured reason. */
  setPreviewError: (info?: PreviewErrorInfo | null) => void

  // ---- Direct status setter (rare; for tests or recovery flows) ----
  setPreviewStatus: (status: CartPreviewStatus) => void

  // ---- Partial-payment broadcast slot ----
  /** Push partial-payment state from CheckoutModal into the cart so the
   *  CFD can render "Cash -$10" rows + remaining balance. CheckoutModal
   *  is the source of truth (it has the live receipt response); the store
   *  just holds the latest snapshot so the broadcast pipeline picks it up.
   *  Pass `null` for both fields to clear. */
  setPartialPayments: (
    payments: CartPartialPayment[] | null,
    amount_to_pay: string | null,
  ) => void

  // ---- Loyalty broadcast slot ----
  /** Push loyalty state (rewards-available flag + staged redemption credit)
   *  from CustomerHeader into the cart so the CFD can render the "Rewards
   *  Available" badge and "Loyalty −$X" / "To collect" rows. CustomerHeader
   *  is the source of truth (it has the SpringBig query + staged redemption +
   *  posConfig to resolve percent redemptions); the store just holds the
   *  latest snapshot so the broadcast pipeline picks it up. Pass `null` to
   *  clear (e.g. when the customer is removed). */
  setLoyalty: (loyalty: CartLoyalty | null) => void

  // ---- Receipt-level discount ----
  /** Apply a cashier-supplied discount to the whole cart. The next
   *  /cart/preview includes it (the fingerprint changes, so the hook
   *  refires automatically); /cart/commit carries it through so the
   *  resulting receipt has the discount baked in. Pass `null` to remove.
   *
   *  Mutually exclusive with per-line discounts on the wire — setting a
   *  cart-level discount causes per-line `discount_amount` to be dropped
   *  from the request payload (we keep the values on the lines so the
   *  cashier can switch back). */
  setDiscount: (discount: CartDiscount | null) => void

  // ---- Per-line discount (legacy `discount_item` scope) ----
  /** Apply a dollar discount to a single cart line, identified by
   *  (product_id, lot_number) -- the same key used everywhere else for
   *  line dedup. Pass `discount_amount: null` to remove. The BE wire
   *  contract is dollars-only; the modal converts percent → amount before
   *  calling this. */
  setLineDiscount: (
    productId: number,
    lotNumber: string | null,
    args: {
      discount_amount: number | null
      reason_id?: number | null
      reason_other?: string | null
    },
  ) => void

  // ---- Manager approvals (over-limit discounts) ----
  /** Record a manager's approval of one discount value. The PIN then rides
   *  every cart request carrying that exact value (see lib/discountLimit.ts
   *  for the transport). Call only after the approval was priced OK. */
  addDiscountApproval: (approval: DiscountApproval) => void
  /** Drop every approval (customer change, clerk switch, rejected PIN). */
  clearDiscountApprovals: () => void
  /** Drop the approvals with these signatures (an approval whose dialog was
   *  closed before it resolved). */
  removeDiscountApprovals: (signatures: readonly string[]) => void
  /** Drop approvals whose tuple is no longer on the cart (see
   *  lib/discountLimit pruneApprovals). `eligibleBaseCents`: the receipt base
   *  of a preview priced for exactly the current cart. */
  pruneDiscountApprovals: (opts?: { eligibleBaseCents?: number | null }) => void
  /** A request carrying the cart was refused by the discount gate: a refused
   *  PIN drops every approval; a 409 discount_exceeds_limit drops only the
   *  approvals for the tuple it names (`extra`); a 429 pin_throttled keeps
   *  them (the backend lets a recently-approved PIN through the throttle). */
  handleGateError: (code: string | null | undefined, extra?: Record<string, unknown> | null) => void
  /** Hold an approval/probe preview for the cart the caller is about to
   *  apply, so the preview hook reuses it instead of re-pricing the same
   *  basket. `(null, null)` drops it. Single use; expires after
   *  PRIMED_PREVIEW_TTL_MS. */
  primePreview: (fingerprint: string | null, preview: PreviewResponse | null) => void
  /** The primed preview when it was priced for exactly `fingerprint` (and is
   *  fresh); consumes it either way. */
  takePrimedPreview: (fingerprint: string) => PreviewResponse | null
  /** posStore.currentReceiptId changed (the bridge calls this). Moving to
   *  another BE receipt -- recall/unhold via posStore.loadReceiptIntoCart,
   *  a new sale, a detach -- drops the approvals and committed rows, which
   *  belonged to the previous one. Binding to the receipt this cart just
   *  committed is not a move: a partially-paid receipt keeps re-previewing
   *  with its approvals. */
  receiptRebound: (receiptId: number | null) => void
}

type CartStore = Cart & CartActions & {
  /** Manager approvals for over-limit discounts, bound to their value.
   *  MEMORY ONLY: `partialize` below is a whitelist that leaves this out, so
   *  a PIN never reaches localStorage. */
  discountApprovals: readonly DiscountApproval[]
  /** `discount_overrides` of the receipt this cart last committed (commit
   *  response), for "approved by" when the preview carries none. Memory
   *  only; dropped when posStore.currentReceiptId leaves that receipt. */
  committedOverrides: { receiptId: number; rows: DiscountOverrideRow[] } | null
  /** See primePreview. Memory only. */
  primedPreview: PrimedPreview<PreviewResponse> | null
  /** Classified reason for the last preview failure; null unless
   *  previewStatus === 'error'. Deliberately NOT persisted (partialize omits
   *  it) — a stale error reason must not survive a refresh; the next preview
   *  re-derives it. */
  previewError: PreviewErrorInfo | null
}

const STORAGE_KEY = 'waio-pos-v2-cart'

/**
 * Build a state object representing an empty cart. Spread on the actions
 * to construct the initial store state.
 */
const initialCartState = (): Cart => emptyCart()

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      ...initialCartState(),
      previewError: null,
      discountApprovals: [],
      committedOverrides: null,
      primedPreview: null,

      // ------------------------------------------------------------------
      // Mutators
      // ------------------------------------------------------------------

      addLine: ({ product, qty = 1, lot_number = null }) => {
        const lines = get().lines.slice()
        // Deduplication: if a line already exists for the same
        // (product_id, lot_number), bump its qty rather than create a
        // second line. Matches BE behavior in ops_receipt.add_items
        // (group_items SysConfig is honored server-side; this is the
        // common case).
        const existingIdx = lines.findIndex(
          (l) => l.product_id === product.id && l.lot_number === lot_number,
        )
        if (existingIdx >= 0) {
          const existing = lines[existingIdx]
          lines[existingIdx] = {
            ...existing,
            qty: existing.qty + qty,
            // Reset to pending so the UI shows the indicator while preview
            // refreshes; preserve `priced` so totals don't blank during the
            // round-trip.
            state: 'pending',
          }
        } else {
          const unitPrice =
            typeof product.price === 'number'
              ? product.price
              : parseFloat(String(product.price ?? 0)) || 0
          lines.push(
            newCartLine({
              product_id: product.id,
              qty,
              lot_number,
              display: {
                description:
                  product.description ?? product.display_name ?? product.name ?? '',
                image: product.image ?? product.image_url ?? null,
                estimated_price: unitPrice,
              },
            }),
          )
        }
        // Keep the previous `preview` so the displayed totals don't blank
        // mid-edit; just flip status so the UI can render an "updating"
        // pill. CartSummary computes a local-delta-adjusted estimate from
        // the stale preview's tax/subtotal ratio until the new preview
        // comes back.
        set({ lines, previewStatus: 'stale' })
      },

      updateQty: (localId, qty) => {
        if (qty <= 0) {
          get().removeLine(localId)
          return
        }
        const lines = get().lines.map((l) =>
          l.localId === localId ? { ...l, qty, state: 'pending' as const } : l,
        )
        set({ lines, previewStatus: 'stale' })
      },

      removeLine: (localId) => {
        const lines = get().lines.filter((l) => l.localId !== localId)
        if (lines.length === 0) {
          // Empty cart: drop preview state too so the UI clears.
          // Partial-payment context goes with it — there's no cart left
          // to apply a partial against, and the next sale must start
          // clean.
          set({
            lines: [],
            preview: null,
            previewStatus: 'idle',
            previewedAt: null,
            partialPayments: null,
            partialAmountToPay: null,
            previewError: null,
          })
          return
        }
        set({ lines, previewStatus: 'stale' })
      },

      setCustomer: (customer_id) => {
        if (get().customer_id === customer_id) return
        // All lines flip to pending: customer change repipes pricing for
        // every line via /cart/preview.
        const lines = get().lines.map((l) => ({ ...l, state: 'pending' as const }))
        set({ customer_id, lines, previewStatus: 'stale' })
      },

      setExemptTaxes: (exempt) => {
        if (get().exempt_taxes === exempt) return
        const lines = get().lines.map((l) => ({ ...l, state: 'pending' as const }))
        set({
          exempt_taxes: exempt,
          lines,
          previewStatus: 'stale',
        })
      },

      clear: () => {
        set({
          ...initialCartState(),
          previewError: null,
          discountApprovals: [],
          committedOverrides: null,
          primedPreview: null,
        })
      },

      loadFromUnhold: (lines, customer_id) => {
        // Held receipts arrive with their stored prices; we keep them in
        // `priced` so the cart shows the held totals immediately.
        // Partial-payment context resets — if the held receipt has
        // partials the next CheckoutModal open will repopulate them.
        // Discount is dropped: held receipts that already had a discount
        // applied carry it server-side; the next preview will reconcile.
        // (When the BE wires receipt-level discount into /cart/preview
        // response, we should seed `discount` here from the held receipt.)
        set({
          lines,
          customer_id,
          exempt_taxes: false,
          preview: null,
          previewStatus: 'idle',
          previewedAt: null,
          partialPayments: null,
          partialAmountToPay: null,
          discount: null,
          previewError: null,
          // A recalled receipt's approvals live server-side (the override
          // rows); nothing approved on the previous cart carries over.
          // (No callers today: the live recall/unhold path is
          // posStore.loadReceiptIntoCart, handled via receiptRebound.)
          discountApprovals: [],
        })
      },

      // ------------------------------------------------------------------
      // Preview lifecycle
      // ------------------------------------------------------------------

      setPreviewPending: () => {
        // We don't track `pendingFingerprint` on the cart itself -- the hook
        // owns it. We just flip the status so the UI can show the spinner.
        if (get().previewStatus !== 'fetching') {
          set({ previewStatus: 'fetching' })
        }
      },

      applyPreview: (preview, firedFingerprint, currentFingerprint) => {
        if (firedFingerprint !== currentFingerprint) {
          // Late resolution for a stale shape -- drop. The next preview
          // (for the current shape) is already in flight or queued.
          return
        }
        const lines = applyPreviewToLines(get().lines, preview)
        set({
          lines,
          preview,
          previewStatus: 'idle',
          previewedAt: Date.now(),
          // Success clears any prior failure reason so the banner/button
          // don't linger on a stale error once Tier 1 snaps back.
          previewError: null,
        })
      },

      setPreviewError: (info = null) => {
        const cur = get()
        if (!info) {
          // Flip to 'error' without disturbing a previously-captured reason.
          if (cur.previewStatus !== 'error') set({ previewStatus: 'error' })
          return
        }
        // Dedupe by value. The 4s self-heal loop re-classifies on every failed
        // attempt and hands us a FRESH object each time; without this guard the
        // new reference would re-render every previewError subscriber
        // (CartSummary) every 4s for the whole outage, even though nothing
        // changed. Only a genuine change of reason (e.g. transient -> rejected)
        // writes through.
        const same =
          cur.previewStatus === 'error'
          && cur.previewError !== null
          && cur.previewError.kind === info.kind
          && cur.previewError.status === info.status
          && cur.previewError.code === info.code
          && cur.previewError.message === info.message
        if (same) return
        set({ previewStatus: 'error', previewError: info })
      },

      setPreviewStatus: (status) => set({ previewStatus: status }),

      setPartialPayments: (payments, amount_to_pay) => {
        // No-op when nothing actually changed — avoids a redundant
        // broadcast frame each render of CheckoutModal.
        const cur = get()
        if (
          cur.partialPayments === payments &&
          cur.partialAmountToPay === amount_to_pay
        ) {
          return
        }
        set({
          partialPayments: payments,
          partialAmountToPay: amount_to_pay,
        })
      },

      setLoyalty: (loyalty) => {
        // No-op when nothing changed — avoids a redundant broadcast frame
        // on every CustomerHeader render. Compare by value since the caller
        // builds a fresh object each render.
        const cur = get().loyalty
        if (
          cur === loyalty ||
          (cur !== null &&
            loyalty !== null &&
            cur.available === loyalty.available &&
            cur.stagedAmount === loyalty.stagedAmount)
        ) {
          return
        }
        set({ loyalty })
      },

      setDiscount: (discount) => {
        // Same partial-payment lock as the posStore cart mutators.
        // A cart-level discount re-prices every line via the next
        // preview, which shifts the cart total against a BE that
        // already has payment JTs riding on the previous shape.
        // DiscountModal is the FE caller; gating here covers any
        // future caller too. Refund mode excluded.
        const ps = usePOSStore.getState()
        if (isReceiptPartiallyPaid(ps.serverTotals, ps.isRefundMode)) return
        // Mark preview stale so CartSummary's tier-2 path keeps the cashier
        // looking at the previous totals (now slightly off until preview
        // returns) rather than blanking. The fingerprint change kicks the
        // /cart/preview hook on the next render.
        set({ discount, previewStatus: discount === get().discount ? get().previewStatus : 'stale' })
        get().pruneDiscountApprovals()
      },

      setLineDiscount: (productId, lotNumber, args) => {
        // Same partial-payment lock. Per-line discount changes
        // subtotals on that line, drift against the BE-charged
        // ledger. DiscountModal in line-scope is the FE caller.
        const ps = usePOSStore.getState()
        if (isReceiptPartiallyPaid(ps.serverTotals, ps.isRefundMode)) return
        const lines = get().lines.map((l) => {
          if (l.product_id !== productId || l.lot_number !== lotNumber) return l
          return {
            ...l,
            discount_amount: args.discount_amount,
            discount_reason_id: args.reason_id ?? null,
            discount_reason_other: args.reason_other ?? null,
            // Bump back to pending so the line renders the spinner pip
            // until the next preview confirms the discounted subtotal.
            state: 'pending' as const,
          }
        })
        set({ lines, previewStatus: 'stale' })
        get().pruneDiscountApprovals()
      },

      addDiscountApproval: (approval) => {
        // addedAt/seen: pruneApprovals keeps it through the apply that
        // follows (the value isn't on the cart yet when it is recorded).
        set({
          discountApprovals: addApproval(get().discountApprovals, {
            ...approval,
            addedAt: Date.now(),
            seen: false,
          }),
        })
      },

      removeDiscountApprovals: (signatures) => {
        const cur = get().discountApprovals
        const next = removeApprovalsBySignature(cur, signatures)
        if (next !== cur) set({ discountApprovals: next })
      },

      clearDiscountApprovals: () => {
        if (get().discountApprovals.length > 0) set({ discountApprovals: [] })
      },

      pruneDiscountApprovals: (opts) => {
        const cur = get().discountApprovals
        const next = pruneApprovals(cur, get(), Date.now(), opts)
        if (next !== cur) set({ discountApprovals: next })
      },

      handleGateError: (code, extra) => {
        // A 429 pin_throttled keeps the riding approvals: the backend lets a
        // recently-approved PIN through its throttle (only the dialog locks).
        if (code === PIN_THROTTLED) return
        if (isOverrideErrorCode(code)) {
          get().clearDiscountApprovals()
          return
        }
        if (code === DISCOUNT_EXCEEDS_LIMIT) {
          const cur = get().discountApprovals
          const next = dropRefusedApprovals(cur, extra)
          if (next !== cur) set({ discountApprovals: next })
        }
      },

      primePreview: (fingerprint, preview) => {
        set({
          primedPreview: fingerprint != null && preview != null
            ? { fingerprint, preview, at: Date.now() }
            : null,
        })
      },

      receiptRebound: (receiptId) => {
        if (keepApprovalsOnRebind(receiptId, get().committedOverrides?.receiptId ?? null)) return
        get().clearDiscountApprovals()
        if (get().committedOverrides || get().primedPreview) {
          set({ committedOverrides: null, primedPreview: null })
        }
      },

      takePrimedPreview: (fingerprint) => {
        const p = get().primedPreview
        if (!p) return null
        set({ primedPreview: null })
        return consumePrimedPreview(p, fingerprint, Date.now())
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Persist what survives a refresh; everything else is recomputable.
      partialize: (state) => ({
        customer_id: state.customer_id,
        exempt_taxes: state.exempt_taxes,
        lines: state.lines,
        discount: state.discount,
        // Persist the last preview so totals/tax survive a refresh. On
        // rehydrate it's marked 'stale' and re-confirmed; if that refresh
        // preview fails, this keeps the cart on the last server-priced
        // numbers (Tier 1/2) instead of dropping tax to $0 (Tier 3).
        preview: state.preview,
        previewedAt: state.previewedAt,
      }) as Partial<CartStore>,
      // On rehydrate, keep the persisted preview but mark it 'stale' so the
      // next preview cycle re-confirms it (success -> 'idle'/Tier 1; failure
      // -> 'error', and the stale preview still feeds Tier 1/2 so tax never
      // collapses to $0). 'stale' (not 'fetching') means the Checkout button
      // isn't gated and no permanent spinner can hang -- the status always
      // resolves to 'idle' or 'error'. With no persisted preview there's
      // nothing to show, so settle to 'idle' (Tier 3 estimating) as before.
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.previewStatus = state.preview ? 'stale' : 'idle'
          // Lines come back from storage with whatever state they were in;
          // refresh to pending so the next preview cycle confirms them.
          state.lines = state.lines.map((l) => ({ ...l, state: 'pending' as const }))
        }
      },
    },
  ),
)

// cartToRequest reads the live approvals from here (see api/cart.ts).
setDiscountApprovalSource(() => useCartStore.getState().discountApprovals)
// ...and the bound receipt id for /cart/preview (see fetchCartPreview).
setPreviewReceiptIdSource(() => usePOSStore.getState().currentReceiptId ?? null)

// The committed receipt's approval rows, for "approved by" (see
// committedOverrides). Runs inside useCommitCart's mutationFn, i.e. BEFORE
// CheckoutModal binds currentReceiptId to that receipt, so receiptRebound
// recognises the bind as "ours" and keeps the approvals.
setCommittedReceiptSink((receipt) => {
  const id = Number(receipt?.id)
  if (!Number.isFinite(id) || id <= 0) return
  const rows = receipt?.discount_overrides
  useCartStore.setState({
    committedOverrides: { receiptId: id, rows: Array.isArray(rows) ? rows : [] },
  })
})

// Clerk switch (or sign-out) drops every approval: a manager's PIN approved a
// discount for the clerk who asked, not for whoever signs in next.
useSessionStore.subscribe((state, prev) => {
  if ((state.staff?.id ?? null) !== (prev.staff?.id ?? null)) {
    useCartStore.getState().clearDiscountApprovals()
  }
})
